import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowRight, FaCode, FaMobile, FaGamepad, FaPalette } from 'react-icons/fa';
import SEO, { siteUrl } from '../components/SEO';

const homeJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'ProfessionalService',
  name: 'RedRender',
  url: siteUrl,
  logo: `${siteUrl}/logo.png`,
  image: `${siteUrl}/logo.png`,
  telephone: '+91 7990813060',
  email: 'info@redrender.in',
  areaServed: 'Worldwide',
  address: {
    '@type': 'PostalAddress',
    addressCountry: 'IN'
  },
  sameAs: ['https://www.linkedin.com/company/redrender'],
  serviceType: [
    'Web Development',
    'Mobile App Development',
    'Game Development',
    'AR/VR Development',
    '3D Visualization',
    'Virtual Showroom Development'
  ]
};

const Home: React.FC = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // 3D Particle Animation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    class Particle {
      x: number;
      y: number;
      z: number;
      vx: number;
      vy: number;
      vz: number;

      constructor() {
        this.x = Math.random() * canvas!.width;
        this.y = Math.random() * canvas!.height;
        this.z = Math.random() * 1000;
        this.vx = (Math.random() - 0.5) * 0.5;
        this.vy = (Math.random() - 0.5) * 0.5;
        this.vz = Math.random() * 2 + 1;
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;
        this.z -= this.vz;

        if (this.z < 1) {
          this.z = 1000;
          this.x = Math.random() * canvas!.width;
          this.y = Math.random() * canvas!.height;
        }
      }

      draw() {
        const scale = 1000 / (1000 + this.z);
        const x2d = (this.x - canvas!.width / 2) * scale + canvas!.width / 2;
        const y2d = (this.y - canvas!.height / 2) * scale + canvas!.height / 2;
        const radius = 2 * scale;

        ctx!.beginPath();
        ctx!.arc(x2d, y2d, radius, 0, Math.PI * 2);
        ctx!.fillStyle = `rgba(239, 68, 68, ${1 - this.z / 1000})`;
        ctx!.fill();
      }
    }

    const particles: Particle[] = [];
    for (let i = 0; i < 100; i++) {
      particles.push(new Particle());
    }

    let animationId: number;
    const animate = () => {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.1)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      particles.forEach(p => {
        p.update();
        p.draw();
      });

      animationId = requestAnimationFrame(animate);
    };

    animate();

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const features = [
    { Icon: FaCode, title: 'Web Development', desc: 'Modern, responsive websites', path: '/services' },
    { Icon: FaMobile, title: 'Mobile Apps', desc: 'iOS & Android solutions', path: '/services' },
    { Icon: FaGamepad, title: 'Game Dev', desc: 'PC & Mobile games', path: '/services' },
    { Icon: FaPalette, title: 'Design', desc: 'Logos & branding', path: '/services' }
  ];

  return (
    <>
      <SEO
        title="RedRender - Web, Mobile, Game, AR/VR & 3D Development Services"
        description="RedRender builds premium web apps, mobile apps, games, AR/VR experiences, 3D visualization tools, virtual showrooms, and interactive digital products."
        path="/"
        keywords={[
          'RedRender',
          'web development company',
          'mobile app development',
          'game development',
          'AR VR development',
          '3D visualization',
          'virtual showroom development'
        ]}
        jsonLd={homeJsonLd}
      />
      <section className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-black via-gray-900 to-black">
      {/* 3D Canvas Background */}
      <canvas ref={canvasRef} className="absolute inset-0 z-0" />

      {/* Animated Gradient Orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div 
          className="absolute w-96 h-96 bg-red-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-float"
          style={{
            top: '10%',
            left: '10%',
            transform: `translate(${mousePosition.x * 0.02}px, ${mousePosition.y * 0.02}px)`,
          }}
        ></div>
        <div 
          className="absolute w-96 h-96 bg-red-600 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-float"
          style={{
            top: '60%',
            right: '10%',
            animationDelay: '2s',
            transform: `translate(${mousePosition.x * -0.02}px, ${mousePosition.y * -0.02}px)`,
          }}
        ></div>
        <div 
          className="absolute w-96 h-96 bg-red-400 rounded-full mix-blend-multiply filter blur-3xl opacity-15 animate-float"
          style={{
            bottom: '10%',
            left: '50%',
            animationDelay: '4s',
            transform: `translate(${mousePosition.x * 0.01}px, ${mousePosition.y * 0.01}px)`,
          }}
        ></div>
      </div>

      {/* Grid Pattern Overlay */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:50px_50px] pointer-events-none"></div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="text-white space-y-8 animate-slide-up">
            {/* Badge */}
            <div className="inline-flex items-center space-x-2 bg-red-500/10 border border-red-500/30 rounded-full px-5 py-2 backdrop-blur-sm group hover:bg-red-500/20 transition-all duration-300 cursor-pointer">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
              </span>
              <span className="text-red-500 font-semibold text-sm font-display">Welcome to RedRender</span>
            </div>
            
            {/* Main Heading */}
            <h1 className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-bold leading-tight font-display">
              Transform Your
              <span className="block mt-2 bg-gradient-to-r from-red-500 via-red-400 to-red-500 bg-clip-text text-transparent bg-[length:200%_100%] animate-gradient">
                Digital Vision
              </span>
            </h1>
            
            {/* Description */}
            <p className="text-lg sm:text-xl text-gray-400 max-w-2xl leading-relaxed">
              We craft exceptional web apps, mobile applications, immersive games, AR/VR experiences, 
              and stunning designs that bring your ideas to life with precision and creativity.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4 pt-4">
              <Link
                to="/contact"
                className="group relative px-8 py-4 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-full font-semibold overflow-hidden shadow-lg hover:shadow-red-500/50 transition-all duration-300"
              >
                <span className="relative z-10 flex items-center gap-2">
                  Start Your Project
                  <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-red-600 to-red-700 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
              </Link>
              <Link
                to="/services"
                className="px-8 py-4 bg-white/5 backdrop-blur-sm border-2 border-white/20 text-white rounded-full font-semibold hover:bg-white/10 hover:border-white/40 transition-all duration-300"
              >
                Explore Services
              </Link>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 pt-8">
              {[
                { number: '100+', label: 'Projects Done' },
                { number: '50+', label: 'Happy Clients' },
                { number: '24/7', label: 'Support' }
              ].map((stat, index) => (
                <div key={index} className="group">
                  <h3 className="text-3xl sm:text-4xl font-bold text-red-500 font-display group-hover:scale-110 transition-transform">
                    {stat.number}
                  </h3>
                  <p className="text-gray-400 text-sm sm:text-base mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right Content - 3D Floating Cards */}
          <div className="hidden lg:grid grid-cols-2 gap-6 relative perspective-1000">
            {features.map((item, index) => (
              <Link
                key={index}
                to={item.path}
                className={`group relative bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl p-8 rounded-3xl border border-white/10 hover:border-red-500/50 transition-all duration-500 transform hover:-translate-y-3 hover:shadow-2xl hover:shadow-red-500/20 ${
                  index % 2 === 1 ? 'mt-12' : index === 2 ? '-mt-12' : ''
                }`}
                style={{ 
                  animationDelay: `${index * 0.1}s`,
                  transform: `rotateX(${mousePosition.y * 0.01}deg) rotateY(${mousePosition.x * 0.01}deg)`
                }}
              >
                {/* Glow Effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-red-500/0 to-red-500/0 group-hover:from-red-500/10 group-hover:to-red-500/5 rounded-3xl transition-all duration-500"></div>
                
                <div className="relative">
                  <item.Icon className="text-5xl text-red-500 mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300" />
                  <h3 className="text-xl font-bold text-white mb-2 font-display">{item.title}</h3>
                  <p className="text-gray-400 text-sm">{item.desc}</p>
                </div>

                {/* Corner Accent */}
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-red-500/20 to-transparent rounded-bl-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce z-10">
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center p-2">
          <div className="w-1 h-3 bg-red-500 rounded-full animate-pulse"></div>
        </div>
      </div>
      </section>
    </>
  );
};

export default Home;
